import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, Medal, Award, Target, TrendingUp } from "lucide-react";

type Category = 'power_bi' | 'sql' | 'python';

interface RankingEntry {
  user_id: string;
  category: Category;
  total_attempts: number;
  correct_attempts: number;
  accuracy_percentage: number;
  total_points: number;
  profile?: {
    full_name: string | null;
    logo_url: string | null;
  };
}

const categoryConfig: Record<Category, { label: string; icon: string; color: string }> = {
  power_bi: { label: 'Power BI', icon: '⚡', color: 'bg-amber-500' },
  sql: { label: 'SQL', icon: '🗄️', color: 'bg-blue-500' },
  python: { label: 'Python', icon: '🐍', color: 'bg-yellow-500' },
};

const categories: Category[] = ['power_bi', 'sql', 'python'];

export function TopicRankings() {
  const [rankings, setRankings] = useState<Record<Category, RankingEntry[]>>({} as Record<Category, RankingEntry[]>);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<Category>('sql');

  useEffect(() => {
    fetchRankings();
  }, []);

  const fetchRankings = async () => {
    setLoading(true);
    try {
      // Fetch admin IDs to filter from ranking
      const { data: adminRoles } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'admin');
      const { data: adminProfiles } = await supabase
        .from('profiles')
        .select('id')
        .eq('is_admin', true);
      const adminIds = new Set([
        ...(adminRoles?.map(r => r.user_id) || []),
        ...(adminProfiles?.map(p => p.id) || []),
      ]);

      const { data, error } = await supabase
        .from('topic_rankings')
        .select(`
          user_id,
          category,
          total_attempts,
          correct_attempts,
          accuracy_percentage,
          total_points
        `)
        .gte('total_attempts', 3)
        .order('total_points', { ascending: false });

      if (error) throw error;

      // Fetch profiles for all users
      const userIds = [...new Set(data?.map(r => r.user_id) || [])];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, logo_url')
        .in('id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Group by category
      const grouped: Record<Category, RankingEntry[]> = {} as Record<Category, RankingEntry[]>;
      categories.forEach(cat => {
        grouped[cat] = (data || [])
          .filter(r => r.category === cat && !adminIds.has(r.user_id))
          .slice(0, 10)
          .map(r => ({
            ...r,
            category: r.category as Category,
            profile: profileMap.get(r.user_id) as { full_name: string | null; logo_url: string | null } | undefined
          }));
      });

      setRankings(grouped);
    } catch (error) {
      console.error('Error fetching rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (position: number) => {
    switch (position) {
      case 0:
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case 1:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 2:
        return <Award className="h-5 w-5 text-amber-600" />;
      default:
        return <span className="text-sm font-medium text-muted-foreground w-5 text-center">{position + 1}</span>;
    }
  };

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          Ranking por Categoria
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeCategory} onValueChange={(v) => setActiveCategory(v as Category)}>
          <TabsList className="grid grid-cols-3 mb-4">
            {categories.map(cat => (
              <TabsTrigger key={cat} value={cat} className="text-xs px-2">
                <span className="mr-1">{categoryConfig[cat].icon}</span>
                <span className="hidden sm:inline">{categoryConfig[cat].label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          {categories.map(cat => (
            <TabsContent key={cat} value={cat} className="mt-0">
              {rankings[cat]?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Nenhum ranking ainda para {categoryConfig[cat].label}</p>
                  <p className="text-sm">Complete pelo menos 3 questões para aparecer aqui!</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {rankings[cat]?.map((entry, index) => (
                    <div
                      key={entry.user_id}
                      className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                        index === 0 ? 'bg-yellow-500/10 border border-yellow-500/30' :
                        index === 1 ? 'bg-gray-500/10 border border-gray-500/30' :
                        index === 2 ? 'bg-amber-500/10 border border-amber-500/30' :
                        'bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center justify-center w-8">
                        {getRankIcon(index)}
                      </div>

                      <Avatar className="h-10 w-10">
                        <AvatarImage src={entry.profile?.logo_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {getInitials(entry.profile?.full_name)}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {entry.profile?.full_name || 'Usuário Anônimo'}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{entry.total_attempts} questões</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {entry.accuracy_percentage.toFixed(0)}% acerto
                          </span>
                        </div>
                      </div>

                      <Badge variant="secondary" className="font-bold">
                        {entry.total_points} pts
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
